import React, { useState, useMemo, useCallback } from 'react';
import { MapModalState, FilterType } from './types/hospital.types';
import { useHospitalData } from './hooks/useHospitalData';
import { useHospitalEdit } from './hooks/useHospitalEdit';
import { useExpandableCards } from './hooks/useExpandableCards';
import HospitalStats from './components/HospitalStats/HospitalStats';
import HospitalFilters from './components/HospitalFilters/HospitalFilters';
import Pagination from './components/Pagination/Pagination';
import HospitalList from './components/HospitalList/HospitalList';
import InteractiveMapModal from './components/InteractiveMapModal/InteractiveMapModal';
import './HospitalMaintenance.css';



const HospitalMaintenance: React.FC = () => {
  const itemsPerPage = 10;

  const {
    hospitals,
    loading,
    totalCount,
    activeCount,
    esicCount,
    emergencyCount,
    searchResultsCount,
    searchTerm,
    filter,
    currentPage,
    isDeleted,
    insuranceFilter,
    setSearchTerm,
    setFilter,
    setCurrentPage,
    setIsDeleted,
    setInsuranceFilter,
    updateLocalHospital,
    fetchCounts,
    totalPages
  } = useHospitalData(itemsPerPage);

  const {
    editingCard,
    saving,
    editFormData,
    hasChanges,
    handleInputChange,
    toggleEdit
  } = useHospitalEdit();

  const { toggleExpand, isExpanded } = useExpandableCards();

  const [mapModal, setMapModal] = useState<MapModalState>({
    isOpen: false,
    coordinates: [0, 0],
    hospitalName: '',
    hospitalId: ''
  });

  const handleFilterChange = useCallback((newFilter: FilterType): void => {
    setFilter(newFilter);
  }, [setFilter]);

  const handleSearchChange = useCallback((term: string): void => {
    setSearchTerm(term);
  }, [setSearchTerm]);

  const handleDeletedChange = useCallback((deleted: boolean): void => {
    setIsDeleted(deleted);
  }, [setIsDeleted]);

  const handleInsuranceChange = useCallback((insurance: string): void => {
    setInsuranceFilter(insurance);
  }, [setInsuranceFilter]);

  const handlePageChange = useCallback((page: number): void => {
    setCurrentPage(page);
  }, [setCurrentPage]);

  const handleMapOpen = useCallback((modal: MapModalState): void => {
    setMapModal(modal);
  }, []);

  const handleMapClose = useCallback((): void => {
    setMapModal(prev => ({ ...prev, isOpen: false }));
  }, []);

  const handleCoordinatesChange = useCallback((newCoordinates: [number, number]): void => {
    if (editingCard === mapModal.hospitalId) {
      const roundedCoordinates: [number, number] = [
        Number(newCoordinates[0].toFixed(7)),
        Number(newCoordinates[1].toFixed(7))
      ];
      handleInputChange('coordinates', roundedCoordinates.join(', '));
      updateLocalHospital(mapModal.hospitalId, {
        location: { type: 'Point', coordinates: roundedCoordinates }
      });
    }
  }, [handleInputChange, updateLocalHospital, mapModal.hospitalId, editingCard]);

  const memoizedHospitalList = useMemo(() => (
    <HospitalList
      hospitals={hospitals}
      loading={loading}
      editingCard={editingCard}
      saving={saving}
      editFormData={editFormData}
      hasChanges={hasChanges}
      isExpanded={isExpanded}
      onToggleExpand={toggleExpand}
      onToggleEdit={toggleEdit}
      onInputChange={handleInputChange}
      onMapOpen={handleMapOpen}
      onUpdateHospital={updateLocalHospital}
      onRefreshCounts={fetchCounts}
    />
  ), [hospitals, loading, editingCard, saving, editFormData, hasChanges, isExpanded, toggleExpand, toggleEdit, handleInputChange, handleMapOpen, updateLocalHospital, fetchCounts]);



  return (
    <div className="hospital-maintenance">
      <div className="header">
        <div className="header-content">
          <h1>Hospital Management Dashboard</h1>
        </div>
      </div>

      <HospitalStats
        totalCount={totalCount}
        activeCount={activeCount}
        esicCount={esicCount}
        emergencyCount={emergencyCount}
        searchResultsCount={searchResultsCount}
        currentFilter={filter}
        onFilterChange={handleFilterChange}
      />

      <HospitalFilters
        filter={filter}
        searchTerm={searchTerm}
        isDeleted={isDeleted}
        insuranceFilter={insuranceFilter}
        onFilterChange={handleFilterChange}
        onSearchChange={handleSearchChange}
        onDeletedChange={handleDeletedChange}
        onInsuranceChange={handleInsuranceChange}
      />

      {memoizedHospitalList}

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />

      <InteractiveMapModal
        isOpen={mapModal.isOpen}
        onClose={handleMapClose}
        initialCoordinates={mapModal.coordinates}
        hospitalName={mapModal.hospitalName}
        onCoordinatesChange={handleCoordinatesChange}
      />
    </div>
  );
};

export default HospitalMaintenance;